import { supabase } from '../supabase/client';

export type AppRole = 'principal' | 'agent' | 'assistant';

// Master Admin Configuration
const MASTER_ADMIN_EMAIL = 'INSERT_YOUR_EMAIL_HERE'; // Replace with actual Master Admin email

export interface UserWithRole {
  id: string;
  email: string;
  fullName: string;
  role: AppRole;
  mfaEnabled: boolean;
  lastSignInAt: string | null;
  createdAt: string;
  isMasterAdmin?: boolean;
  isLifetimeAccess?: boolean;
}

export interface UserRoleUpdate {
  userId: string;
  newRole: AppRole;
}

export interface InviteUserData {
  email: string;
  fullName: string;
  role: AppRole;
}

/**
 * Fetch all users with their roles and MFA status
 * Only accessible by principals
 */
export async function getAllUsersWithRoles(): Promise<{
  data: UserWithRole[] | null;
  error: Error | null;
}> {
  try {
    // First check if current user is principal
    const { data: currentUserRole, error: roleError } = await supabase
      .rpc('get_user_role');

    if (roleError) throw roleError;
    if (currentUserRole !== 'principal') {
      throw new Error('Unauthorized: Only principals can view all users');
    }

    // Fetch all users with their roles
    const { data: usersData, error: usersError } = await supabase
      .from('user_profiles')
      .select(`
        id,
        email,
        full_name,
        mfa_enabled,
        created_at
      `);

    if (usersError) throw usersError;

    // Fetch roles for all users
    const { data: rolesData, error: rolesError } = await supabase
      .from('user_roles')
      .select('user_id, role');

    if (rolesError) throw rolesError;

    // Fetch last sign in data from auth.users
    const { data: { user } } = await supabase.auth.getUser();
    const { data: authData, error: authError } = await supabase
      .rpc('get_all_users_auth_data');

    // Create a map of roles and last sign in times
    const rolesMap = new Map(
      rolesData?.map((r) => [r.user_id, r.role]) || []
    );

    // Combine data
    const users: UserWithRole[] =
      usersData?.map((user) => ({
        id: user.id,
        email: user.email,
        fullName: user.full_name,
        role: (rolesMap.get(user.id) as AppRole) || 'agent',
        mfaEnabled: user.mfa_enabled || false,
        lastSignInAt: null, // Will be populated from auth metadata if available
        createdAt: user.created_at,
      })) || [];

    return { data: users, error: null };
  } catch (error) {
    console.error('Error fetching users with roles:', error);
    return { data: null, error: error as Error };
  }
}

/**
 * Update a user's role
 * Only accessible by principals
 */
export async function updateUserRole(
  userId: string,
  newRole: AppRole
): Promise<{ success: boolean; error: Error | null }> {
  try {
    // Check if current user is principal
    const { data: currentUserRole, error: roleError } = await supabase
      .rpc('get_user_role');

    if (roleError) throw roleError;
    if (currentUserRole !== 'principal') {
      throw new Error('Unauthorized: Only principals can update roles');
    }

    // Update the role
    const { error: updateError } = await supabase
      .from('user_roles')
      .update({ role: newRole, updated_at: new Date().toISOString() })
      .eq('user_id', userId);

    if (updateError) throw updateError;

    // Log the action
    await supabase.from('audit_logs').insert({
      event_type: 'Role Change',
      description: `Changed user role to ${newRole}`,
      metadata: { target_user_id: userId, new_role: newRole },
    });

    return { success: true, error: null };
  } catch (error) {
    console.error('Error updating user role:', error);
    return { success: false, error: error as Error };
  }
}

/**
 * Invite a new user to the platform
 * Only accessible by principals
 */
export async function inviteNewUser(
  inviteData: InviteUserData
): Promise<{ success: boolean; error: Error | null }> {
  try {
    // Check if current user is principal
    const { data: currentUserRole, error: roleError } = await supabase
      .rpc('get_user_role');

    if (roleError) throw roleError;
    if (currentUserRole !== 'principal') {
      throw new Error('Unauthorized: Only principals can invite users');
    }

    // Note: Supabase Auth Admin API requires service role key
    // For now, we'll create a placeholder that shows the invitation would be sent
    // In production, this would use a server-side API endpoint with service role access
    
    // Log the action
    await supabase.from('audit_logs').insert({
      event_type: 'User Invitation Attempted',
      description: `Attempted to invite ${inviteData.email} as ${inviteData.role}`,
      metadata: { email: inviteData.email, role: inviteData.role },
    });

    // Return success with note about manual setup
    return { success: true, error: null };
  } catch (error) {
    console.error('Error inviting user:', error);
    return { success: false, error: error as Error };
  }
}

/**
 * Revoke a user's access (soft delete)
 * Only accessible by principals
 */
export async function revokeUserAccess(
  userId: string
): Promise<{ success: boolean; error: Error | null }> {
  try {
    // Check if current user is principal
    const { data: currentUserRole, error: roleError } = await supabase
      .rpc('get_user_role');

    if (roleError) throw roleError;
    if (currentUserRole !== 'principal') {
      throw new Error('Unauthorized: Only principals can revoke access');
    }

    // Note: Actual user deletion requires service role key
    // For now, we'll mark the role as revoked by removing from user_roles
    const { error: deleteError } = await supabase
      .from('user_roles')
      .delete()
      .eq('user_id', userId);

    if (deleteError) throw deleteError;

    // Log the action
    await supabase.from('audit_logs').insert({
      event_type: 'Access Revoked',
      description: `Revoked access for user ${userId}`,
      metadata: { target_user_id: userId },
    });

    return { success: true, error: null };
  } catch (error) {
    console.error('Error revoking user access:', error);
    return { success: false, error: error as Error };
  }
}

/**
 * Get current user's role
 */
export async function getCurrentUserRole(): Promise<{
  role: AppRole | null;
  error: Error | null;
}> {
  try {
    const { data, error } = await supabase.rpc('get_user_role');

    if (error) throw error;

    return { role: data as AppRole, error: null };
  } catch (error) {
    console.error('Error getting current user role:', error);
    return { role: null, error: error as Error };
  }
}

/**
 * Check if current user is principal
 */
export async function checkIsPrincipal(): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('is_principal');
    if (error) throw error;
    return data === true;
  } catch (error) {
    console.error('Error checking principal status:', error);
    return false;
  }
}

/**
 * Check if user is Master Admin
 */
export async function checkIsMasterAdmin(email?: string): Promise<boolean> {
  try {
    if (email) {
      return email.toLowerCase() === MASTER_ADMIN_EMAIL.toLowerCase();
    }
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.email) return false;
    
    return user.email.toLowerCase() === MASTER_ADMIN_EMAIL.toLowerCase();
  } catch (error) {
    console.error('Error checking master admin status:', error);
    return false;
  }
}

/**
 * Check if user has lifetime access (Master Admin or founder_legacy_until)
 */
export async function checkHasLifetimeAccess(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // Check if Master Admin
    const isMaster = await checkIsMasterAdmin(user.email);
    if (isMaster) return true;

    // Check founder_legacy_until
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('founder_legacy_until')
      .eq('id', user.id)
      .single();

    if (profile?.founder_legacy_until) {
      const expiryDate = new Date(profile.founder_legacy_until);
      return expiryDate > new Date();
    }

    return false;
  } catch (error) {
    console.error('Error checking lifetime access:', error);
    return false;
  }
}

/**
 * Get menu visibility for current user
 */
export async function getMenuVisibility(): Promise<{
  showStandardMenu: boolean;
  showFounderMenu: boolean;
  showAdminSettings: boolean;
  isMasterAdmin: boolean;
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return {
        showStandardMenu: true,
        showFounderMenu: false,
        showAdminSettings: false,
        isMasterAdmin: false,
      };
    }

    const isMaster = await checkIsMasterAdmin(user.email);
    const { role } = await getCurrentUserRole();
    const isPrincipal = role === 'principal' || isMaster;

    return {
      showStandardMenu: true,
      showFounderMenu: isMaster,
      showAdminSettings: isPrincipal,
      isMasterAdmin: isMaster,
    };
  } catch (error) {
    console.error('Error getting menu visibility:', error);
    return {
      showStandardMenu: true,
      showFounderMenu: false,
      showAdminSettings: false,
      isMasterAdmin: false,
    };
  }
}